/**
 * Mock ApiClient — same surface as ./client.ts ApiClient, but every read /
 * write maps to the in-memory `DemoStore` from @field-iq/mock-demo. Used when
 * VITE_MOCK_MODE === 'true' so the dashboard runs as a standalone Vercel build
 * with no backend.
 *
 * Mutations (postNote, createEquipment, …) update the store snapshot so the
 * Live Sessions / Session Detail / Admin pages stay reactive via useMockSync.
 */
import {
  getDemoStore,
  MOCK_EQUIPMENT,
  MOCK_PROCEDURE,
  STEPS,
  type DemoSnapshot,
} from '@field-iq/mock-demo';
import type { SessionListQuery } from './client';
import type { ReplayEvent, ReplayResponse } from './replay';
import type {
  AuditRow,
  CertificateResponse,
  EquipmentRow,
  GenesisImportResult,
  GenesisPreview,
  ListSessionsResponse,
  OverviewStats,
  ProcedureRow,
  SessionDetailResponse,
  SessionStatus,
  StepRow,
  UserRole,
  UserRow,
} from './types';

function snapshot(): DemoSnapshot {
  return getDemoStore().getSnapshot();
}

function sessionRow(snap: DemoSnapshot) {
  return {
    id: snap.session.id,
    orgId: snap.session.orgId,
    equipmentId: snap.session.equipmentId,
    procedureId: snap.session.procedureId,
    procedureVersion: snap.session.procedureVersion,
    technicianUserId: snap.session.technicianUserId,
    status: snap.session.status as SessionStatus,
    startedAt: snap.session.startedAt,
    completedAt: snap.session.completedAt,
    // The names travel with the row so the list and the detail page agree.
    // Without them the detail page read "Technician" over "Equipment".
    technicianName: snap.trainee.fullName,
    equipmentName: MOCK_EQUIPMENT.name,
    equipmentAssetTag: MOCK_EQUIPMENT.assetTag,
    procedureName: MOCK_PROCEDURE.name,
    totalSteps: STEPS.length,
    verifiedSteps: snap.audit.filter((a) => a.eventType === 'verified' && !a.supersededBy).length,
    retries: snap.audit.filter((a) => a.eventType === 'retry').length,
  };
}

function stepRows(): StepRow[] {
  return STEPS.map((s) => ({
    id: `step-${s.stepNumber}`,
    stepNumber: s.stepNumber,
    title: s.title,
    instruction: s.instruction,
    referenceImageUrl: s.photoDataUri,
    verificationPrompt: s.verificationPrompt,
    successCriteria: s.verdictMessage,
    retryThreshold: 3,
  }));
}

/** The audit log grades confidence in words; the replay wants a number. */
function confidenceValue(c: 'high' | 'medium' | 'low' | null): number {
  return c === 'high' ? 0.92 : c === 'medium' ? 0.72 : c === 'low' ? 0.5 : 0.8;
}

function auditRows(snap: DemoSnapshot): AuditRow[] {
  return snap.audit.map((a) => ({
    id: a.id,
    sessionId: a.sessionId,
    stepId: a.stepId,
    stepNumber: a.stepNumber,
    eventType: a.eventType,
    photoUrl: a.photoUrl,
    verified: a.verified,
    confidence: a.confidence,
    message: a.message,
    detail: a.detail,
    timestamp: a.timestamp,
    supersededBy: a.supersededBy,
  }));
}

const noteRows = (snap: DemoSnapshot): AuditRow[] =>
  snap.notes.map((n) => ({
    id: n.id,
    sessionId: snap.session.id,
    stepId: null,
    stepNumber: n.stepNumber ?? null,
    eventType: 'note',
    photoUrl: null,
    verified: null,
    confidence: null,
    message: null,
    detail: n.text,
    timestamp: n.timestamp,
    supersededBy: null,
  }));

export class MockApiClient {
  readonly apiHost = 'mock://demo';

  async listSessions(query: SessionListQuery = {}): Promise<ListSessionsResponse> {
    const snap = snapshot();
    const row = sessionRow(snap);
    if (query.status && row.status !== query.status) return { sessions: [] };
    return { sessions: [row] };
  }

  async requestReport(_id: string): Promise<{ queued: boolean }> {
    return { queued: true };
  }

  async getCertificate(_id: string): Promise<CertificateResponse> {
    throw new Error('Certificates are not generated in demo mode.');
  }

  async overrideVerdict(): Promise<{ auditId: string }> {
    return { auditId: `override-${Date.now()}` };
  }

  async getOverviewStats(days: number): Promise<OverviewStats> {
    const snap = snapshot();
    const verified = snap.audit.filter((a) => a.eventType === 'verified' && !a.supersededBy);
    const retries = snap.audit.filter((a) => a.eventType === 'retry');
    const today = new Date().toISOString().slice(0, 10);
    const completed = snap.session.status === 'completed' ? 1 : 0;
    return {
      windowDays: days,
      since: new Date(Date.now() - days * 86_400_000).toISOString(),
      totals: {
        sessions: 1,
        active: snap.session.status === 'active' ? 1 : 0,
        completed,
        failed: 0,
        abandoned: 0,
        completionRate: completed ? 1 : null,
        firstPassRate:
          verified.length > 0 ? (verified.length - retries.length) / verified.length : null,
        avgDurationSeconds: completed ? 540 : null,
        verifiedSteps: verified.length,
        retries: retries.length,
        technicians: 1,
      },
      perDay: [{ date: today, started: 1, completed, failed: 0 }],
      perStep: STEPS.map((st) => {
        const v = verified.filter((a) => a.stepNumber === st.stepNumber).length;
        const r = retries.filter((a) => a.stepNumber === st.stepNumber).length;
        return {
          stepNumber: st.stepNumber,
          title: st.title,
          verified: v,
          retries: r,
          firstPass: Math.max(0, v - r),
          firstPassRate: v > 0 ? Math.max(0, v - r) / v : null,
        };
      }),
      technicians: [
        {
          userId: snap.session.technicianUserId,
          name: snap.trainee.fullName,
          sessions: 1,
          completed,
          verifiedSteps: verified.length,
          retries: retries.length,
          firstPassRate:
            verified.length > 0 ? (verified.length - retries.length) / verified.length : null,
        },
      ],
      equipment: [
        {
          equipmentId: MOCK_EQUIPMENT.id,
          name: MOCK_EQUIPMENT.name,
          assetTag: MOCK_EQUIPMENT.assetTag,
          sessions: 1,
          completed,
        },
      ],
      recentVerdicts: [...snap.audit]
        .filter((a) => a.eventType === 'verified' || a.eventType === 'retry')
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        .slice(0, 12)
        .map((a) => ({
          auditId: a.id,
          sessionId: a.sessionId,
          stepNumber: a.stepNumber,
          verified: a.verified,
          confidence: a.confidence,
          message: a.message,
          timestamp: a.timestamp,
          technicianName: snap.trainee.fullName,
          equipmentName: MOCK_EQUIPMENT.name,
        })),
    };
  }

  private userRows: UserRow[] = [
    {
      id: 'u-carlos',
      email: 'carlos.romero@eonreality.com',
      fullName: 'Carlos Romero',
      role: 'trainer',
      isActive: true,
      createdAt: '2026-01-10T09:00:00Z',
      lastLoginAt: new Date().toISOString(),
    },
    {
      id: 'u-priya',
      email: 'priya.patel@eonreality.com',
      fullName: 'Priya Patel',
      role: 'supervisor',
      isActive: true,
      createdAt: '2026-01-10T09:00:00Z',
      lastLoginAt: null,
    },
    {
      id: 'u-maya',
      email: 'maya.wu@eonreality.com',
      fullName: 'Maya Wu',
      role: 'technician',
      isActive: true,
      createdAt: '2026-01-10T09:00:00Z',
      lastLoginAt: new Date().toISOString(),
    },
  ];

  async listUsers(): Promise<{ users: UserRow[] }> {
    return { users: this.userRows };
  }
  async createUser(body: { email: string; fullName: string; role: UserRole }): Promise<UserRow> {
    const row: UserRow = {
      id: `u-${Date.now()}`,
      email: body.email,
      fullName: body.fullName,
      role: body.role,
      isActive: true,
      createdAt: new Date().toISOString(),
      lastLoginAt: null,
    };
    this.userRows = [...this.userRows, row];
    return row;
  }
  async updateUser(
    id: string,
    body: { fullName?: string; role?: UserRole; isActive?: boolean },
  ): Promise<UserRow> {
    const row = this.userRows.find((u) => u.id === id);
    if (!row) throw new Error('User not found');
    const next = { ...row, ...body };
    this.userRows = this.userRows.map((u) => (u.id === id ? next : u));
    return next;
  }
  async deleteEquipment(id: string): Promise<{ ok: boolean }> {
    this.equipmentRows = this.equipmentRows.filter((e) => e.id !== id);
    return { ok: true };
  }

  async getSession(_id: string): Promise<SessionDetailResponse> {
    const snap = snapshot();
    return {
      session: sessionRow(snap),
      steps: stepRows(),
      state: { currentStepNumber: snap.currentStep, status: snap.session.status as SessionStatus },
    };
  }

  async getSessionAudit(_id: string): Promise<{ auditLog: AuditRow[] }> {
    const snap = snapshot();
    const combined = [...auditRows(snap), ...noteRows(snap)].sort((a, b) =>
      a.timestamp.localeCompare(b.timestamp),
    );
    return { auditLog: combined };
  }

  async postNote(_id: string, text: string, _stepNumber?: number): Promise<{ auditId: string }> {
    const note = getDemoStore().addNote(text);
    return { auditId: note.id };
  }

  private equipmentRows: EquipmentRow[] = [
    {
      id: MOCK_EQUIPMENT.id,
      orgId: 'org_eon_demo',
      name: MOCK_EQUIPMENT.name,
      assetTag: MOCK_EQUIPMENT.assetTag,
      qrCodeValue: MOCK_EQUIPMENT.qrCodeValue,
      description: 'DAC Worldwide #811 trainer — used for hands-on LOTO certification.',
      location: 'EON Training Bay 1',
    },
  ];
  private procedureRows: ProcedureRow[] = [
    {
      id: MOCK_PROCEDURE.id,
      equipmentId: MOCK_EQUIPMENT.id,
      name: MOCK_PROCEDURE.name,
      version: MOCK_PROCEDURE.version,
      totalSteps: STEPS.length,
      isActive: true,
    },
  ];

  async listEquipment(): Promise<EquipmentRow[]> {
    return this.equipmentRows;
  }
  async resolveEquipment(
    qrValue: string,
  ): Promise<{ equipment: EquipmentRow; activeProcedure: ProcedureRow | null }> {
    const equipment = this.equipmentRows.find((r) => r.qrCodeValue === qrValue);
    if (!equipment) throw new Error('No equipment for that QR value');
    const activeProcedure =
      this.procedureRows.find((p) => p.equipmentId === equipment.id) ?? this.procedureRows[0]!;
    return { equipment, activeProcedure };
  }
  async listProcedureSteps(_procedureId: string): Promise<{ steps: StepRow[] }> {
    return {
      steps: STEPS.map((s) => ({
        id: `step-${s.stepNumber}`,
        stepNumber: s.stepNumber,
        title: s.title,
        instruction: s.instruction,
        verificationPrompt: s.verificationPrompt,
        referenceImageUrl: s.stepNumber % 3 === 0 ? null : (s.photoDataUri ?? null),
      })),
    };
  }
  async createEquipment(body: Partial<EquipmentRow>): Promise<EquipmentRow> {
    const row: EquipmentRow = {
      id: `eq-${Date.now()}`,
      orgId: 'org_eon_demo',
      name: body.name ?? 'Untitled',
      assetTag: body.assetTag ?? '',
      qrCodeValue: body.qrCodeValue ?? '',
      description: body.description,
      location: body.location,
    };
    this.equipmentRows = [...this.equipmentRows, row];
    return row;
  }
  async genesisPreview(projectId: string): Promise<GenesisPreview> {
    return {
      projectId,
      procedureTitle: 'Pump Skid P-204 Lockout/Tagout',
      safetyLevel: 'critical',
      sourceVersion: 3,
      totalSteps: 12,
      stepsWithRenderedImages: 12,
      componentCount: 6,
      qrCodeValue: `GENESIS-${projectId}`,
    };
  }
  async genesisImport(body: {
    projectId: string;
    refresh?: boolean;
  }): Promise<GenesisImportResult> {
    const preview = await this.genesisPreview(body.projectId);
    const existing = this.equipmentRows.find((r) => r.qrCodeValue === preview.qrCodeValue);
    const row =
      existing ??
      (await this.createEquipment({
        name: preview.procedureTitle,
        assetTag: `GEN-${body.projectId.slice(0, 8).toUpperCase()}`,
        qrCodeValue: preview.qrCodeValue,
        description: `Imported from EON Genesis (project ${body.projectId})`,
      }));
    return {
      ok: true,
      durationMs: 420,
      import: { status: existing ? 'unchanged' : 'imported', snapshotId: 'snap-demo', version: 1 },
      materialized: {
        equipmentId: row.id,
        procedureId: 'proc-demo',
        procedureCreated: !existing,
        stepsInserted: existing ? 0 : preview.totalSteps,
        stepsUpdated: existing && body.refresh ? preview.totalSteps : 0,
      },
      summary: {
        procedureTitle: preview.procedureTitle,
        totalSteps: preview.totalSteps,
        stepsWithRenderedImages: preview.stepsWithRenderedImages,
      },
      equipment: { id: row.id, name: row.name, qrCodeValue: row.qrCodeValue },
    };
  }
  async createProcedure(body: Partial<ProcedureRow>): Promise<ProcedureRow> {
    const row: ProcedureRow = {
      id: `proc-${Date.now()}`,
      equipmentId: body.equipmentId ?? MOCK_EQUIPMENT.id,
      name: body.name ?? 'Untitled procedure',
      version: body.version ?? '0.1.0',
      totalSteps: 0,
      isActive: true,
    };
    this.procedureRows = [...this.procedureRows, row];
    return row;
  }
  async updateStep(_id: string, body: Partial<StepRow>): Promise<StepRow> {
    return {
      id: _id,
      stepNumber: body.stepNumber ?? 1,
      title: body.title ?? 'STEP',
      instruction: body.instruction ?? '',
      referenceImageUrl: body.referenceImageUrl ?? null,
      verificationPrompt: body.verificationPrompt ?? null,
      successCriteria: body.successCriteria ?? null,
      retryThreshold: body.retryThreshold ?? 3,
    };
  }
  async testPrompt(
    stepId: string,
    body: { prompt: string; photoBase64?: string },
  ): Promise<{
    stepId: string;
    mode: 'mock';
    result: { verified: boolean; confidence: 'high'; message: string; detail: string };
  }> {
    return {
      stepId,
      mode: 'mock',
      result: {
        verified: true,
        confidence: 'high',
        message: 'Sandbox verdict (mock).',
        detail: `Mock pass for a prompt of length ${body.prompt.length}. Live verdicts require services/verifier + ANTHROPIC_API_KEY.`,
      },
    };
  }

  /**
   * The replay, built from the same audit trail the detail page reads.
   *
   * Demo mode has no backend to ask, and a Replay tab that spins forever is
   * worse than one that shows the run it already has in memory.
   */
  async getReplay(_id: string): Promise<ReplayResponse> {
    const snap = snapshot();
    const events: ReplayEvent[] = [];
    for (const a of snap.audit) {
      const common = { id: a.id, stepNumber: a.stepNumber, timestamp: a.timestamp };
      switch (a.eventType) {
        case 'start':
          events.push({ ...common, type: 'session.started', payload: {} as Record<string, never> });
          break;
        case 'photo_submitted':
          if (a.photoUrl) {
            events.push({
              ...common,
              type: 'step.photo_captured',
              payload: { photoUrl: a.photoUrl, capturedAt: a.timestamp },
            });
          }
          break;
        case 'verified':
          events.push({
            ...common,
            type: 'step.verified',
            payload: {
              verdict: 'pass',
              confidence: confidenceValue(a.confidence),
              verdictText: a.message ?? '',
            },
          });
          break;
        case 'error':
          events.push({
            ...common,
            type: 'step.verified',
            payload: {
              verdict: 'fail',
              confidence: confidenceValue(a.confidence),
              verdictText: a.message ?? a.detail ?? '',
            },
          });
          break;
        case 'retry':
          events.push({
            ...common,
            type: 'step.retry',
            payload: { reason: a.message ?? '', previousVerdictText: a.detail ?? '' },
          });
          break;
        case 'complete':
          events.push({ ...common, type: 'session.ended', payload: {} as Record<string, never> });
          break;
        default:
          break;
      }
    }
    return {
      session: {
        id: snap.session.id,
        workerId: snap.session.technicianUserId,
        workerName: snap.trainee.fullName,
        procedureId: snap.session.procedureId,
        procedureTitle: `${snap.equipment.name} — ${snap.procedure.name}`,
        startedAt: snap.session.startedAt,
        endedAt: snap.session.completedAt,
        status:
          snap.session.status === 'completed'
            ? 'completed'
            : snap.session.status === 'active'
              ? 'active'
              : 'aborted',
        finalOutcome: snap.session.status === 'completed' ? 'pass' : null,
      },
      events,
    };
  }
}
